import { Chart, useChart } from '@chakra-ui/charts';
import {
  Badge,
  Box,
  Card,
  Checkbox,
  Flex,
  HStack,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  SegmentGroup,
  Select,
  SelectItem,
  SimpleGrid,
  Table,
  Text,
  VStack,
  createListCollection,
  useSelectContext,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { JSX, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSearch, FaSortAlphaDown, FaSortAlphaUp, FaSortNumericDown, FaSortNumericUp } from 'react-icons/fa';
import { FaChartLine, FaCheck, FaRegCalendar } from 'react-icons/fa6';
import { FiTarget } from 'react-icons/fi';
import { HiOutlineTrendingDown, HiOutlineTrendingUp } from 'react-icons/hi';
import { IoBarChartOutline } from 'react-icons/io5';
import { MdClose } from 'react-icons/md';
import { RiForbidLine } from 'react-icons/ri';
import { Bar, BarChart, CartesianGrid, LabelList, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';

import { IndicatorsService } from '@/client';
import Loader from '@/components/Loader';
import { stringToColor } from '@/utils';

type graphType = {
  value: string;
  label: string;
  icon: JSX.Element;
};

const SelectTrigger = () => {
  const select = useSelectContext();
  const items = select.selectedItems as graphType[];

  return (
    <IconButton px="2" variant="outline" size="sm" {...select.getTriggerProps()}>
      {select.hasSelectedItems ? items[0].icon : <RiForbidLine />}
    </IconButton>
  );
};

function getIndicatorYearSettingsSummaryQueryOptions({ slug, indicatorId }: { slug: string; indicatorId: string }) {
  return {
    queryKey: ['indicator_year_settings_summary', [slug, indicatorId]],
    queryFn: async () => {
      return await IndicatorsService.readIndicatorSummary({
        companySlug: slug,
        id: indicatorId,
      });
    },
  };
}
type EntitySortKey = 'value' | 'objective' | 'entity_name';
type EntitySortableField = EntitySortKey | `-${EntitySortKey}`;

export const IndicatorCharts = ({ slug, indicatorId }: { slug: string; indicatorId: string }) => {
  const [chartType, setChartType] = useState<string | null>('perYear');
  const [selectedYear, setSelectedYear] = useState<number[]>([]);
  const [searchEntityValue, setSearchEntityValue] = useState<string>('');
  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);
  const [hideConsolidated, setHideConsolidated] = useState(false);
  const [hideObjective, setHideObjective] = useState(false);
  const [sortEntityTable, setSortEntityTable] = useState<EntitySortableField>('entity_name');

  const iconOptions = createListCollection<graphType>({
    items: [
      {
        value: 'barchart',
        label: 'Bar Chart',
        icon: <IoBarChartOutline />,
      },
      {
        value: 'linechart',
        label: 'Line Chart',
        icon: <FaChartLine />,
      },
    ],
  });

  const [selectedChartType, setSelectedChartType] = useState<graphType>(iconOptions.items[0]);

  const { t } = useTranslation();
  const { data: indicatorYearSettingsSummary, isLoading } = useQuery({
    ...getIndicatorYearSettingsSummaryQueryOptions({ slug, indicatorId }),
  });

  const yearsInstantiated = [
    ...(indicatorYearSettingsSummary?.consolidated?.map((y) => y.year) || []),
    ...(indicatorYearSettingsSummary?.entities?.map((y) => y.year) || []),
  ];
  const uniqueYearsInstantiated = [...new Set(yearsInstantiated)].sort((a, b) => b - a);
  const instantiatedYearCollection = createListCollection({
    items: uniqueYearsInstantiated.map((year) => ({
      label: year.toString(),
      value: year.toString(),
    })),
  });

  useEffect(() => {
    if (uniqueYearsInstantiated.length && selectedYear.length === 0) {
      setSelectedYear([uniqueYearsInstantiated.at(0)!]);
    }
  }, [uniqueYearsInstantiated]);

  const fnSortEntityTable = (a: any, b: any) => {
    const isDescending = sortEntityTable?.startsWith('-');
    const sortField = (sortEntityTable || 'entity_name').replace('-', '') as EntitySortKey;

    const aRaw = a[sortField];
    const bRaw = b[sortField];

    const aVal = typeof aRaw === 'number' ? aRaw : parseFloat(aRaw?.toString() ?? '') || 0;
    const bVal = typeof bRaw === 'number' ? bRaw : parseFloat(bRaw?.toString() ?? '') || 0;

    if (!isNaN(aVal) && !isNaN(bVal)) {
      // Sort as number
      return isDescending ? bVal - aVal : aVal - bVal;
    }

    // Fallback to string sort
    const aStr = aRaw?.toString() ?? '';
    const bStr = bRaw?.toString() ?? '';
    return isDescending ? bStr.localeCompare(aStr) : aStr.localeCompare(bStr);
  };

  const filteredEntityForSelectedYear = useMemo(() => {
    if (!indicatorYearSettingsSummary?.entities) return [];
    const entities = indicatorYearSettingsSummary.entities;
    const entityMap = new Map();
    entities.forEach((el) => {
      const existing = entityMap.get(el.entity_id);
      if (!existing || el.year === selectedYear[0]) {
        entityMap.set(el.entity_id, el);
      }
    });

    const result = Array.from(entityMap.values())
      .map((el) => {
        const yearData = entities.find((e) => e.entity_id === el.entity_id && e.year === selectedYear[0]);
        return {
          entity_id: el.entity_id,
          entity_name: el.entity_name,
          value: yearData?.value ?? null,
          objective: yearData?.objective ?? null,
          year: selectedYear[0],
        };
      })
      .filter((el) => el.entity_name.toLowerCase().includes(searchEntityValue.toLowerCase()))
      .sort(fnSortEntityTable);

    return result;
  }, [indicatorYearSettingsSummary, selectedYear, searchEntityValue, fnSortEntityTable]);
  const unit = indicatorYearSettingsSummary?.unit;
  const currentObjective = indicatorYearSettingsSummary?.consolidated?.find(
    (el) => el.year === selectedYear[0]
  )?.objective;
  const currentValue = indicatorYearSettingsSummary?.consolidated?.find((el) => el.year === selectedYear[0])?.value;
  const currentReferenceValue = indicatorYearSettingsSummary?.consolidated?.find(
    (el) => el.year === indicatorYearSettingsSummary.referenceYear
  )?.value;
  const previousYearValue = indicatorYearSettingsSummary?.consolidated?.find(
    (el) => el.year === selectedYear[0] - 1
  )?.value;
  const previousYearEntityValue = (entity_id: string, year: number) =>
    indicatorYearSettingsSummary?.entities?.find((el) => el.entity_id === entity_id && el.year === year)?.value;

  const handleSortEntityTable = (field: EntitySortKey) => {
    setSortEntityTable((prev) => {
      if (prev === field) return `-${field}`; // switch to descending
      return field; // switch to ascending or set default
    });
  };

  const chartData = useMemo<{ data: any[]; series: any[] }>(() => {
    const data: any[] = [];
    const series: any[] = [
      ...(!hideConsolidated ? [{ name: 'value', color: 'green.500', label: 'Valeur' }] : []),
      ...(!hideConsolidated && !hideObjective
        ? [{ name: 'objective', color: 'blue.600', label: 'Objectif', strokeDasharray: '5,5' }]
        : []),
    ];

    if (!indicatorYearSettingsSummary) return { data, series };

    if (chartType === 'perYear') {
      const consolidated = indicatorYearSettingsSummary.consolidated ?? [];
      data.push(
        ...consolidated
          .sort((el1, el2) => el1.year - el2.year)
          .map((el) => ({
            value: el.value ? el.value.toFixed(2) : 'N/R',
            objective: el.objective ? el.objective.toFixed(2) : 'N/R',
            name: el.year.toString(),
          }))
      );
    }

    if (chartType === 'perEntity' && selectedChartType.value === 'linechart') {
      const consolidated = indicatorYearSettingsSummary.consolidated ?? [];
      const entities = indicatorYearSettingsSummary.entities ?? [];

      const dataByYear: Record<string, any> = {};

      if (!hideConsolidated) {
        consolidated.forEach((el) => {
          const year = el.year.toString();
          if (!dataByYear[year]) {
            dataByYear[year] = { name: year };
          }

          dataByYear[year].value = el.value?.toFixed(2) ?? 'N/R';
          dataByYear[year].objective = el.objective?.toFixed(2) ?? 'N/R';
        });
      }

      filteredEntityForSelectedYear.forEach((entity) => {
        if (selectedEntityIds.includes(entity.entity_id)) {
          const color = stringToColor(entity.entity_name);
          series.push(
            {
              name: `${entity.entity_name}-value`,
              label: `${entity.entity_name} - Valeur`,
              color: color + '.600',
            },
            ...(!hideObjective
              ? [
                  {
                    name: `${entity.entity_name}-objective`,
                    label: `${entity.entity_name} - Objectif`,
                    color: color + '.400',
                    strokeDasharray: '5,5',
                  },
                ]
              : [])
          );
        }
      });

      entities.forEach((el) => {
        const year = el.year.toString();
        const entityName = filteredEntityForSelectedYear.find((e) => e.entity_id === el.entity_id)?.entity_name;
        if (!entityName) return;

        if (!dataByYear[year]) {
          dataByYear[year] = { name: year };
        }

        dataByYear[year][`${entityName}-value`] = el.value?.toFixed(2) ?? 'N/R';
        dataByYear[year][`${entityName}-objective`] = el.objective?.toFixed(2) ?? 'N/R';
      });

      data.push(...Object.values(dataByYear));
    }

    if (chartType === 'perEntity' && selectedChartType.value === 'barchart') {
      let consolidated = indicatorYearSettingsSummary.consolidated?.filter((el) => el.year === selectedYear[0]);
      const entities = indicatorYearSettingsSummary.entities
        ?.filter((el) => el.year === selectedYear[0] && selectedEntityIds.includes(el.entity_id))
        .sort(fnSortEntityTable);
      if (consolidated && !hideConsolidated) {
        data.push(
          ...consolidated.map((el) => ({
            value: el.value ? el.value.toFixed(2) : 'N/R',
            objective: el.objective ? el.objective.toFixed(2) : 'N/R',
            name: 'Consolidé',
            color: 'green.500',
          }))
        );
      }
      if (entities) {
        data.push(
          ...entities.map((el) => ({
            value: el.value ? el.value.toFixed(2) : 'N/R',
            objective: el.objective ? el.objective.toFixed(2) : 'N/R',
            name: el.entity_name,
            color: stringToColor(el.entity_name) + '.500',
          }))
        );
      }
      if (hideConsolidated) {
        series.push({ name: 'value', color: 'green.500', label: 'Valeur' });
        if (!hideObjective) {
          series.push({ name: 'objective', color: 'blue.600', label: 'Objectif', strokeDasharray: '5,5' });
        }
      }
    }

    return { data, series };
  }, [
    chartType,
    indicatorYearSettingsSummary,
    selectedYear,
    selectedEntityIds,
    hideConsolidated,
    hideObjective,
    sortEntityTable,
    selectedChartType.value,
  ]);

  const chart = useChart({
    data: chartData.data,
    series: chartData.series,
  });

  const values = chart.data.flatMap((entry) =>
    Object.entries(entry)
      .filter(([key, _]) => /(?:value|objective)$/.test(key))
      .map(([_, value]) => (typeof value === 'number' ? value : Number(value)))
      .filter((v): v is number => !isNaN(v))
  );

  const maxValue = Math.ceil(Math.max(...values) * 1.1) || 1;
  const minValue = Math.floor(Math.min(...values) * 0.9) || 0;

  if (isLoading) {
    return <Loader loadingText={`Chargement des données ...`} />;
  }

  return (
    <>
      <HStack>
        <SegmentGroup.Root
          defaultValue={chartType}
          onValueChange={(e) => {
            setChartType(e.value);
            setHideConsolidated(false);
          }}
          p={1}
          borderRadius={'md'}
          size={'sm'}
        >
          <SegmentGroup.Indicator top={1} left={1} />
          <SegmentGroup.Items
            style={{ cursor: 'pointer' }}
            items={[
              {
                value: 'perYear',
                label: (
                  <HStack fontWeight={'semibold'} color={chartType === 'perYear' ? 'black' : 'gray.500'}>
                    {t('indicator.summary.perYear')}
                  </HStack>
                ),
              },
              ...((indicatorYearSettingsSummary?.entities?.length ?? 0) > 0
                ? [
                    {
                      value: 'perEntity',
                      label: (
                        <HStack fontWeight={'semibold'} color={chartType === 'perEntity' ? 'black' : 'gray.500'}>
                          {t('indicator.summary.perEntity')}
                        </HStack>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </SegmentGroup.Root>
        {selectedYear.length > 0 && (
          <HStack ml={'auto'}>
            {t('collection.header.selectPeriod')}
            <Select.Root
              collection={instantiatedYearCollection}
              value={[selectedYear[0].toString()]}
              size={'sm'}
              width={'78px'}
              onValueChange={(e) => setSelectedYear([parseInt(e.value[0])])}
              gap={1}
            >
              <Select.HiddenSelect />
              <Select.Control>
                <Select.Trigger>
                  <Select.ValueText />
                </Select.Trigger>
                <Select.IndicatorGroup>
                  <Select.Indicator />
                </Select.IndicatorGroup>
              </Select.Control>
              <Select.Positioner>
                <Select.Content>
                  {instantiatedYearCollection.items.map((year) => (
                    <SelectItem item={year} key={year.value}>
                      {year.label}
                    </SelectItem>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
          </HStack>
        )}
      </HStack>

      <Card.Root>
        <Card.Header p={3} flex={'flex'} flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Heading size="sm">{indicatorYearSettingsSummary?.name}</Heading>
          <Box>
            <Select.Root
              collection={iconOptions}
              size="sm"
              width="1.5rem"
              value={[selectedChartType.value]}
              mr={3}
              onValueChange={(e) => {
                setSelectedChartType(iconOptions.items.find((item) => item.value === e.value[0])!);
              }}
              gap={1}
            >
              <Select.HiddenSelect />
              <SelectTrigger />
              <Select.Positioner>
                <Select.Content>
                  {iconOptions.items.map((model) => (
                    <SelectItem item={model} key={model.value}>
                      {model.icon}
                    </SelectItem>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
          </Box>
        </Card.Header>
        <Card.Body p={3}>
          {selectedChartType.value === 'linechart' && (
            <Chart.Root maxH="sm" chart={chart}>
              <LineChart data={chart.data}>
                <CartesianGrid stroke={chart.color('border.muted')} vertical={false} />
                <XAxis
                  axisLine={true}
                  tickLine={true}
                  stroke="#000"
                  dataKey={chart.key('name')}
                  tick={{ fill: '#000' }}
                />
                <YAxis
                  axisLine={true}
                  tickLine={true}
                  stroke="#000"
                  tick={{ fill: '#000' }}
                  domain={[minValue, maxValue]}
                  tickFormatter={(value) => `${value} ${unit}`}
                />
                <Legend content={<Chart.Legend interaction="hover" />} />
                <Tooltip
                  cursor={{ fill: chart.color('bg.muted') }}
                  animationDuration={100}
                  content={<Chart.Tooltip />}
                />
                {chart.series.map((item) => (
                  <Line
                    isAnimationActive={true}
                    key={item.name?.toString()}
                    dataKey={chart.key(item.name?.toString())}
                    fill={chart.color(item.color)}
                    stroke={chart.color(item.color)}
                    strokeWidth={2}
                    connectNulls
                    type="monotone"
                    strokeDasharray={item.strokeDasharray}
                    opacity={chart.getSeriesOpacity(item.name?.toString())}
                  >
                    <LabelList
                      dataKey={chart.key(item.name?.toString())}
                      position="top"
                      style={{ fontWeight: '600', fill: chart.color('fg') }}
                      opacity={chart.getSeriesOpacity(item.name?.toString())}
                    />
                  </Line>
                ))}
              </LineChart>
            </Chart.Root>
          )}
          {selectedChartType.value === 'barchart' && (
            <Chart.Root maxH="sm" chart={chart}>
              <BarChart data={chart.data}>
                <CartesianGrid stroke={chart.color('border.muted')} vertical={false} />
                <XAxis
                  axisLine={true}
                  tickLine={true}
                  stroke="#000"
                  dataKey={chart.key('name')}
                  tick={{ fill: '#000' }}
                />
                <YAxis
                  axisLine={true}
                  tickLine={true}
                  stroke="#000"
                  tick={{ fill: '#000' }}
                  domain={[minValue, maxValue]}
                  tickFormatter={(value) => `${value} ${unit}`}
                />
                <Legend content={<Chart.Legend interaction="hover" />} />
                <Tooltip
                  cursor={{ fill: chart.color('bg.muted') }}
                  animationDuration={100}
                  content={<Chart.Tooltip />}
                />
                {chart.series.map((item) => (
                  <Bar
                    isAnimationActive={true}
                    key={item.name?.toString()}
                    dataKey={chart.key(item.name?.toString())}
                    fill={chart.color(item.color)}
                    stroke={chart.color(item.color)}
                    stackId={item.stackId}
                    opacity={chart.getSeriesOpacity(item.name?.toString())}
                  >
                    <LabelList
                      dataKey={chart.key(item.name?.toString())}
                      position="top"
                      style={{ fontWeight: '600', fill: chart.color('fg') }}
                      opacity={chart.getSeriesOpacity(item.name?.toString())}
                    />
                  </Bar>
                ))}
              </BarChart>
            </Chart.Root>
          )}
          <HStack gap={4}>
            <Checkbox.Root checked={hideObjective} onCheckedChange={(e) => setHideObjective(!!e.checked)}>
              <Checkbox.HiddenInput />
              <Checkbox.Control />
              <Checkbox.Label>Masquer les objectifs</Checkbox.Label>
            </Checkbox.Root>
            {chartType === 'perEntity' && (
              <Checkbox.Root checked={hideConsolidated} onCheckedChange={(e) => setHideConsolidated(!!e.checked)}>
                <Checkbox.HiddenInput />
                <Checkbox.Control />
                <Checkbox.Label>Masquer la valeur consolidée</Checkbox.Label>
              </Checkbox.Root>
            )}
          </HStack>
        </Card.Body>
      </Card.Root>

      <Card.Root>
        <Card.Header p={3}>
          <Heading size="sm">Métriques pour {selectedYear}</Heading>
        </Card.Header>

        <Card.Body px={3} pb={3} pt={0}>
          <SimpleGrid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
            {/* Valeur actuelle */}
            <VStack alignItems={'start'}>
              <Flex align="center" gap={2}>
                <Box w={3} h={3} bg="green.500" borderRadius="full" />
                <Text fontSize="sm" color={'gray.600'}>
                  Valeur réalisée
                </Text>
              </Flex>
              <Text fontSize="2xl" fontWeight="bold" color={'green.600'}>
                {currentValue ? (
                  <>
                    {currentValue?.toFixed(2)}{' '}
                    <Text as="span" fontSize="sm" fontWeight="normal" color="gray.500">
                      {unit}
                    </Text>
                  </>
                ) : (
                  <>
                    <Text as="span" fontSize="sm" fontWeight="normal" color="gray.500">
                      N/A
                    </Text>
                  </>
                )}
              </Text>
              {currentValue && previousYearValue && (
                <Flex align="center" gap={1}>
                  {previousYearValue === 0 ? (
                    <Text fontSize="sm" color="gray.500">
                      Variation non calculable
                    </Text>
                  ) : (
                    <>
                      {currentValue > previousYearValue && <HiOutlineTrendingUp size={16} color="#22c55e" />}
                      {currentValue < previousYearValue && <HiOutlineTrendingDown size={16} color="#ef4444" />}
                      {currentValue === previousYearValue && (
                        <Text fontSize="sm" color="gray.600">
                          Aucun changement
                        </Text>
                      )}
                      {currentValue !== previousYearValue && (
                        <Text fontSize="sm" color={currentValue < previousYearValue ? 'red.600' : 'green.600'}>
                          {(((currentValue - previousYearValue) / previousYearValue) * 100).toFixed(2)}% vs{' '}
                          {selectedYear[0] - 1}
                        </Text>
                      )}
                    </>
                  )}
                </Flex>
              )}
            </VStack>

            {/* Objectif */}
            <VStack alignItems={'start'}>
              <Flex align="center" gap={2}>
                <FiTarget size={12} color="#3b82f6" />
                <Text fontSize="sm" color={'gray.600'}>
                  Objectif
                </Text>
              </Flex>
              <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                {currentObjective ? (
                  <>
                    {currentObjective?.toFixed(2)}{' '}
                    <Text as="span" fontSize="sm" fontWeight="normal" color="gray.500">
                      {unit}
                    </Text>
                  </>
                ) : (
                  <Text as="span" fontSize="sm" fontWeight="normal" color="gray.500">
                    N/R
                  </Text>
                )}
              </Text>
              {currentValue && currentObjective && (
                <Flex align="center" gap={1}>
                  {currentValue >= currentObjective ? (
                    <Badge colorScheme="green" variant="subtle" color="green.600">
                      <FaCheck size={12} color="#22c55e" />
                      Objectif respecté
                    </Badge>
                  ) : (
                    <Badge colorScheme="red" variant="subtle" color="red.600">
                      <MdClose size={12} color="#ef4444" />
                      Objectif non atteint
                    </Badge>
                  )}
                </Flex>
              )}
            </VStack>

            {/* Comparaison avec référence */}
            <VStack alignItems={'start'}>
              <Flex align="center" gap={2}>
                <FaRegCalendar size={12} color="#9333ea" />
                <Text fontSize="sm" color={'gray.600'}>
                  vs Référence ({indicatorYearSettingsSummary?.referenceYear})
                </Text>
              </Flex>
              <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                {currentReferenceValue ? (
                  <>
                    {currentReferenceValue?.toFixed(2)}{' '}
                    <Text as="span" fontSize="sm" fontWeight="normal" color="gray.500">
                      {unit}
                    </Text>
                  </>
                ) : (
                  <>
                    <Text as="span" fontSize="sm" fontWeight="normal" color="gray.500">
                      N/A
                    </Text>
                  </>
                )}
              </Text>
              {currentValue && currentReferenceValue && (
                <Flex align="center" gap={1}>
                  {currentReferenceValue === 0 ? (
                    <Text fontSize="sm" color="gray.500">
                      Variation non calculable
                    </Text>
                  ) : (
                    <>
                      {currentValue > currentReferenceValue && <HiOutlineTrendingUp size={16} color="#ef4444" />}
                      {currentValue < currentReferenceValue && <HiOutlineTrendingDown size={16} color="#22c55e" />}
                      {currentValue === currentReferenceValue && (
                        <Text fontSize="sm" color="gray.600">
                          Aucun changement
                        </Text>
                      )}
                      {currentValue !== currentReferenceValue && (
                        <Text fontSize="sm" color={currentValue < currentReferenceValue ? 'green.600' : 'red.600'}>
                          {(((currentValue - currentReferenceValue) / currentReferenceValue) * 100).toFixed(2)}%
                        </Text>
                      )}
                    </>
                  )}
                </Flex>
              )}
            </VStack>
          </SimpleGrid>
        </Card.Body>
      </Card.Root>

      {chartType === 'perEntity' && filteredEntityForSelectedYear && (
        <Card.Root>
          <Card.Header p={3}>
            <HStack justifyContent={'space-between'}>
              <Heading size="sm">Resultat des entités {`(${filteredEntityForSelectedYear?.length})`}</Heading>
              <InputGroup
                ml={'auto'}
                placeSelf={'end'}
                maxW={'300px'}
                endElement={
                  <Icon cursor={'pointer'} _hover={{ color: 'primary' }}>
                    <FaSearch />
                  </Icon>
                }
              >
                <Input
                  variant="flushed"
                  size="sm"
                  placeholder={t('ui.search')}
                  value={searchEntityValue}
                  onChange={(e) => setSearchEntityValue(e.target.value)}
                />
              </InputGroup>
            </HStack>
          </Card.Header>

          <Card.Body px={3} pb={3} pt={0}>
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader w="6">
                    <Checkbox.Root
                      size="sm"
                      top="0.5"
                      checked={
                        selectedEntityIds.length === filteredEntityForSelectedYear.length
                          ? true
                          : selectedEntityIds.length === 0
                            ? false
                            : 'indeterminate'
                      }
                      onCheckedChange={(e) => {
                        if (e.checked) {
                          setSelectedEntityIds(filteredEntityForSelectedYear.map((item) => item.entity_id));
                        } else {
                          setSelectedEntityIds([]);
                        }
                      }}
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                    </Checkbox.Root>
                  </Table.ColumnHeader>
                  <Table.ColumnHeader onClick={() => handleSortEntityTable('entity_name')}>
                    {sortEntityTable.endsWith('entity_name') &&
                      (!sortEntityTable.startsWith('-') ? (
                        <Icon mr={2}>
                          <FaSortAlphaDown />
                        </Icon>
                      ) : (
                        <Icon mr={2}>
                          <FaSortAlphaUp />
                        </Icon>
                      ))}
                    Nom de l'entité
                  </Table.ColumnHeader>
                  <Table.ColumnHeader onClick={() => handleSortEntityTable('value')}>
                    {sortEntityTable.endsWith('value') &&
                      (!sortEntityTable.startsWith('-') ? (
                        <Icon mr={2}>
                          <FaSortNumericDown />
                        </Icon>
                      ) : (
                        <Icon mr={2}>
                          <FaSortNumericUp />
                        </Icon>
                      ))}
                    Valeur réalisée
                  </Table.ColumnHeader>
                  <Table.ColumnHeader onClick={() => handleSortEntityTable('objective')}>
                    {sortEntityTable.endsWith('objective') &&
                      (!sortEntityTable.startsWith('-') ? (
                        <Icon mr={2}>
                          <FaSortNumericDown />
                        </Icon>
                      ) : (
                        <Icon mr={2}>
                          <FaSortNumericUp />
                        </Icon>
                      ))}
                    Objectif
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredEntityForSelectedYear.map((item) => (
                  <Table.Row key={item.entity_id}>
                    <Table.Cell
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedEntityIds((prev) => {
                          if (prev.includes(item.entity_id)) {
                            return prev.filter((id) => id !== item.entity_id);
                          }
                          return [...prev, item.entity_id];
                        });
                      }}
                    >
                      <Checkbox.Root size="sm" top="0.5" checked={selectedEntityIds.includes(item.entity_id)}>
                        <Checkbox.Control />
                      </Checkbox.Root>
                    </Table.Cell>
                    <Table.Cell>
                      <HStack maxW={'200px'}>{item.entity_name}</HStack>
                    </Table.Cell>
                    <Table.Cell>
                      <VStack alignItems={'start'}>
                        <Text fontSize="xl" fontWeight="bold" color={'green.600'}>
                          {item.value ? (
                            <>
                              {item.value?.toFixed(2)}{' '}
                              <Text as="span" fontSize="sm" fontWeight="normal" color="gray.500">
                                {unit}
                              </Text>
                            </>
                          ) : (
                            <>
                              <Text as="span" fontSize="sm" fontWeight="normal" color="gray.500">
                                N/A
                              </Text>
                            </>
                          )}
                        </Text>
                        {item.value &&
                          (() => {
                            const _previousYearEntityValue = previousYearEntityValue(
                              item.entity_id,
                              selectedYear[0] - 1
                            );
                            if (!_previousYearEntityValue) return;
                            return (
                              <Flex align="center" gap={1}>
                                {previousYearValue === 0 ? (
                                  <Text fontSize="sm" color="gray.500">
                                    Variation non calculable
                                  </Text>
                                ) : (
                                  <>
                                    {item.value > _previousYearEntityValue && (
                                      <HiOutlineTrendingUp size={16} color="#22c55e" />
                                    )}
                                    {item.value < _previousYearEntityValue && (
                                      <HiOutlineTrendingDown size={16} color="#ef4444" />
                                    )}
                                    {item.value === _previousYearEntityValue && (
                                      <Text fontSize="sm" color="gray.600">
                                        Aucun changement
                                      </Text>
                                    )}
                                    {item.value !== _previousYearEntityValue && (
                                      <Text
                                        fontSize="sm"
                                        color={item.value < _previousYearEntityValue ? 'red.600' : 'green.600'}
                                      >
                                        {(
                                          ((item.value - _previousYearEntityValue) / _previousYearEntityValue) *
                                          100
                                        ).toFixed(2)}
                                        % vs {selectedYear[0] - 1}
                                      </Text>
                                    )}
                                  </>
                                )}
                              </Flex>
                            );
                          })()}
                      </VStack>
                    </Table.Cell>
                    <Table.Cell>
                      {item.objective ? (
                        <VStack alignItems={'start'}>
                          <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                            {item.objective ? (
                              <>
                                {item.objective?.toFixed(2)}{' '}
                                <Text as="span" fontSize="sm" fontWeight="normal" color="gray.500">
                                  {unit}
                                </Text>
                              </>
                            ) : (
                              <Text as="span" fontSize="sm" fontWeight="normal" color="gray.500">
                                N/R
                              </Text>
                            )}
                          </Text>
                          {item.value && item.objective && (
                            <Flex align="center" gap={1}>
                              {item.value >= item.objective ? (
                                <Badge colorScheme="green" variant="subtle" color="green.600">
                                  <FaCheck size={12} color="#22c55e" />
                                  Objectif respecté
                                </Badge>
                              ) : (
                                <Badge colorScheme="red" variant="subtle" color="red.600">
                                  <MdClose size={12} color="#ef4444" />
                                  Objectif non atteint
                                </Badge>
                              )}
                            </Flex>
                          )}
                        </VStack>
                      ) : (
                        <Text as="span" fontSize="sm" fontWeight="normal" color="gray.500">
                          N/R
                        </Text>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Card.Body>
        </Card.Root>
      )}
    </>
  );
};
